import { z } from 'zod';

import { setNullToUndefined } from './zod';

export const DEFAULT_PAGE_NUMBER = 1;

export const pageNumberSchema = z.coerce
  .number()
  .int()
  .positive()
  .default(DEFAULT_PAGE_NUMBER);

/**
 * A schema that parses page numbers from strings, converting null to undefined first.
 * This is useful for handling query parameters where the absence of a value should default to 1.
 */
export const pageNumberFromStringSchema = z.preprocess(
  setNullToUndefined,
  pageNumberSchema,
);

export const AVAILABLE_PAGE_SIZES = [10, 20, 50, 100];
export const [DEFAULT_PAGE_SIZE, ..._] = AVAILABLE_PAGE_SIZES;

export const pageSizeSchema = z.coerce
  .number()
  .int()
  .refine((val) => AVAILABLE_PAGE_SIZES.includes(val), {
    message: `Page size must be one of: ${AVAILABLE_PAGE_SIZES.join(', ')}`,
  })
  .default(DEFAULT_PAGE_SIZE);

export const pageSizeFromStringSchema = z.preprocess(
  setNullToUndefined,
  pageSizeSchema,
);

export type PaginationRequest = {
  /**
   * Current page number (1-based)
   */
  pageNumber: number;

  /**
   * Number of items per page
   */
  pageSize: number;
};

export type PaginationResponse = {
  /**
   * Total number of items
   */
  totalItems: number;

  /**
   * Total number of pages
   */
  totalPages: number;
};

export const getSkipTakeFromPagination = (
  pagination: PaginationRequest,
): { skip: number; take: number } => {
  const { pageNumber, pageSize } = pagination;
  const skip = (pageNumber - 1) * pageSize;
  const take = pageSize;

  return { skip, take };
};

export const getPaginationResponse = ({
  totalItems,
  pageSize,
}: {
  totalItems: number;
  pageSize: number;
}) => {
  const totalPages = Math.ceil(totalItems / pageSize);
  return {
    totalItems,
    totalPages,
  };
};

export const safePageNumber = (pageNumber: unknown) => {
  const result = pageNumberFromStringSchema.safeParse(pageNumber);
  return result.success ? result.data : DEFAULT_PAGE_NUMBER;
};

export const safePageSize = (pageSize: unknown) => {
  const result = pageSizeFromStringSchema.safeParse(pageSize);
  return result.success ? result.data : DEFAULT_PAGE_SIZE;
};
